import PropTypes from 'prop-types';
import pick from 'lodash.pick';
import { ContentStateShape } from 'next/lib/shapes';

/**
 * Conditions shape
 */
export const URL = PropTypes.exact({
  url: PropTypes.exact({
    operator: PropTypes.string.isRequired,
    value: PropTypes.string,
  }),
});

export const Domains = PropTypes.exact({
  domains: PropTypes.exact({
    operator: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }),
});

export const Properties = PropTypes.exact({
  properties: PropTypes.exact({
    property: PropTypes.string.isRequired,
    operator: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
});

export const Groups = PropTypes.exact({
  groups: PropTypes.exact({
    property: PropTypes.string.isRequired,
    operator: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
});

export const Languages = PropTypes.exact({
  languages: PropTypes.exact({
    operator: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }),
});

export const Content = PropTypes.exact({
  content: PropTypes.exact({
    content: PropTypes.string.isRequired,
    operator: PropTypes.string,
    times: PropTypes.number,
    value: PropTypes.number,
  }),
});

export const Event = PropTypes.exact({
  event: PropTypes.exact({
    event: PropTypes.string.isRequired,
    operator: PropTypes.string,
    times: PropTypes.number,
    value: PropTypes.number,
  }),
});

export const Segments = PropTypes.exact({
  segments: PropTypes.exact({
    segment: PropTypes.string,
  }),
});

export const Checklists = PropTypes.exact({
  checklists: PropTypes.exact({
    checklist: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['in_progress', 'skipped', 'completed\ndismissed']),
  }),
});

export const Attributes = PropTypes.exact({
  attributes: PropTypes.exact({
    attribute: PropTypes.string.isRequired,
    operator: PropTypes.string.isRequired,
    value: PropTypes.string,
  }),
});

export const Trigger = PropTypes.exact({
  trigger: PropTypes.exact({
    event: PropTypes.string,
  }),
});

export const Screen = PropTypes.exact({
  screen: PropTypes.exact({
    operator: PropTypes.string,
    value: PropTypes.string,
  }),
});

export const Version = PropTypes.exact({
  version: PropTypes.exact({
    operator: PropTypes.string,
    value: PropTypes.string,
  }),
});

export const App = PropTypes.exact({
  app: PropTypes.exact({
    operator: PropTypes.string,
    value: PropTypes.string,
  }),
});

export const And = {};
export const Or = {};
export const Not = {};

export const Conditions = PropTypes.oneOfType([
  URL,
  Domains,
  Properties,
  Groups,
  Languages,
  Content,
  Event,
  Segments,
  Attributes,
  Checklists,
  Trigger,
  Screen,
  Version,
  App,
  // Since the following condition types are self-referential, we'll use
  // PropTypes.exact and a plain object to delay evaluation until runtime.
  PropTypes.exact(And),
  PropTypes.exact(Or),
  PropTypes.exact(Not),
]);

// Finish defining the self-referential types. Note that this requires
// PropTypes.exact to be used every time one of these types is used directly.
And.and = PropTypes.arrayOf(Conditions);
Or.or = PropTypes.arrayOf(Conditions);
Not.not = PropTypes.arrayOf(Conditions);

export const Frequency = PropTypes.oneOf(['every_time', 'never', 'once']);

/**
 * Full shape of rule data model
 */
const Shape = {
  conditions: Conditions,
  contentType: PropTypes.oneOf([
    'journey',
    'satisfaction-survey',
    'experience',
  ]),
  createdAt: PropTypes.number,
  createdBy: PropTypes.string,
  frequency: Frequency,
  goals: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  migratedFromStepId: PropTypes.string,
  name: PropTypes.string,
  nextContentId: PropTypes.string,
  published: PropTypes.bool,
  redirectNewTab: PropTypes.bool,
  redirectUrl: PropTypes.string,
  sortPriority: PropTypes.number,
  source: PropTypes.oneOf(['CONTENT-API', 'CRX', 'MY.APPCUES']),
  state: ContentStateShape,
  testFlowId: PropTypes.string,
  testVersionOf: PropTypes.string,
  throttleExempt: PropTypes.bool,
  unpublishedAt: PropTypes.number,
  unpublishedBy: PropTypes.string,
  updatedAt: PropTypes.number,
  updatedBy: PropTypes.string,
  widgetFilter: PropTypes.oneOf(['exclude', 'include', 'page']),
};

/**
 * Allowed fields for storage
 */
export const allowed = [
  'conditions',
  'id',
  'contentType',
  'sortPriority',
  'published',
  'frequency',
  'goals',
];

/**
 * Rule parsing function
 *
 * @param {Object<keyof Rule>} rule - Object with rule fields
 * @return {Object<keyof Rule>} Object with parsed rule fields
 */
export const parse = rule => pick(rule, allowed);

export default PropTypes.shape(parse(Shape));
